import React from 'react';
import * as d3 from 'd3';
import { useResponsiveD3, useD3Tooltip } from '../../../hooks/useD3';

const WasteFlowChart = ({ data }) => {
  const { showTooltip, hideTooltip } = useD3Tooltip();

  const renderChart = (svg, { width, height }) => {
    const margin = { top: 20, right: 20, bottom: 40, left: 60 };
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    // Create main group
    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Sample data structure for waste flow with better colors
    const wasteFlowData = [
      { stage: 'Collection', amount: data?.totalInput || 0, color: '#2563EB' },
      { stage: 'Processing', amount: (data?.totalInput || 0) * 0.85, color: '#DC2626' },
      { stage: 'Available', amount: data?.currentStock || 0, color: '#059669' },
      { stage: 'Sold', amount: data?.totalOutput || 0, color: '#7C3AED' }
    ];

    // Scales
    const xScale = d3.scaleBand()
      .domain(wasteFlowData.map(d => d.stage))
      .range([0, chartWidth])
      .padding(0.2);

    const yScale = d3.scaleLinear()
      .domain([0, d3.max(wasteFlowData, d => d.amount)])
      .range([chartHeight, 0]);

    // Bars
    g.selectAll('.bar')
      .data(wasteFlowData)
      .enter().append('rect')
      .attr('class', 'bar')
      .attr('x', d => xScale(d.stage))
      .attr('width', xScale.bandwidth())
      .attr('y', chartHeight)
      .attr('height', 0)
      .attr('fill', d => d.color)
      .attr('rx', 4)
      .on('mouseover', function(event, d) {
        d3.select(this).attr('opacity', 0.8);
        const formattedAmount = d.amount >= 1000000 ? 
          `${(d.amount / 1000000).toFixed(1)}M kg` :
          d.amount >= 1000 ? 
          `${(d.amount / 1000).toFixed(0)}k kg` :
          `${d.amount} kg`;
        showTooltip(`${d.stage}: ${formattedAmount}`, event);
      })
      .on('mouseout', function(d) {
        d3.select(this).attr('opacity', 1);
        hideTooltip();
      })
      .transition()
      .duration(800)
      .attr('y', d => yScale(d.amount))
      .attr('height', d => chartHeight - yScale(d.amount));

    // Add value labels on bars with better formatting
    g.selectAll('.label')
      .data(wasteFlowData)
      .enter().append('text')
      .attr('class', 'label')
      .attr('x', d => xScale(d.stage) + xScale.bandwidth() / 2)
      .attr('y', d => yScale(d.amount) - 8)
      .attr('text-anchor', 'middle')
      .style('font-size', '13px')
      .style('font-weight', 'bold')
      .style('fill', '#1F2937')
      .text(d => {
        if (d.amount >= 1000000) {
          return `${(d.amount / 1000000).toFixed(1)}M kg`;
        } else if (d.amount >= 1000) {
          return `${(d.amount / 1000).toFixed(0)}k kg`;
        } else {
          return `${d.amount} kg`;
        }
      });

    // X-axis
    g.append('g')
      .attr('transform', `translate(0,${chartHeight})`)
      .call(d3.axisBottom(xScale))
      .style('font-size', '12px')
      .selectAll('text')
      .style('font-weight', '500')
      .style('fill', '#374151');

    // Y-axis
    g.append('g')
      .call(d3.axisLeft(yScale).tickFormat(d => `${d / 1000}k`))
      .style('font-size', '12px')
      .style('color', '#6B7280');

    // Add horizontal grid lines
    g.append('g')
      .attr('class', 'grid')
      .call(d3.axisLeft(yScale)
        .tickSize(-chartWidth)
        .tickFormat('')
      )
      .style('stroke-dasharray', '3,3')
      .style('opacity', 0.2)
      .style('stroke', '#9CA3AF');

    // Y-axis label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', 0 - margin.left)
      .attr('x', 0 - (chartHeight / 2))
      .attr('dy', '1em')
      .style('text-anchor', 'middle')
      .style('font-size', '12px')
      .style('fill', '#6B7280')
      .text('Waste Amount (kg)');

    // Flow arrows
    for (let i = 0; i < wasteFlowData.length - 1; i++) {
      const x1 = xScale(wasteFlowData[i].stage) + xScale.bandwidth();
      const x2 = xScale(wasteFlowData[i + 1].stage);
      const y = chartHeight / 2;

      g.append('path')
        .attr('d', `M ${x1 + 10} ${y} L ${x2 - 10} ${y}`)
        .attr('stroke', '#9CA3AF')
        .attr('stroke-width', 2)
        .attr('marker-end', 'url(#arrowhead)');
    }

    // Define arrow marker
    svg.append('defs').append('marker')
      .attr('id', 'arrowhead')
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 5)
      .attr('refY', 0)
      .attr('markerWidth', 4)
      .attr('markerHeight', 4)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,-5L10,0L0,5')
      .attr('fill', '#9CA3AF');
  };

  const { containerRef, svgRef } = useResponsiveD3(
    renderChart, 
    [data], 
    2.5
  );

  return (
    <div ref={containerRef} className="w-full h-64">
      <svg ref={svgRef} className="w-full h-full"></svg>
    </div>
  );
};

export default WasteFlowChart;